/**
 * Progress Header Component
 * Displays the header with progress bar for warm-up or challenge mode
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Calendar, Zap, CheckCircle, BookOpen, ChevronDown, School, Radio } from "lucide-react";
import { Button } from "./ui/button";
import { TrackBadge } from "./TrackBadge";

export default function ProgressHeader({
  mode,
  today,
  dailyProgress,
  challengesInfo,
  displayScore,
  user,
  dailyCompleted,
  onBackToWarmup,
  streakCount = 0,
  assignedDrill = null,
  assignedDrills = [],
  onStartDrill = null,
  onPlayCaveBat = null
}) {
  const [showDrillList, setShowDrillList] = useState(false);
  const navigate = useNavigate();

  // Prominent entry point for students whose teacher is running a live drill
  const JoinLiveButton = () => (
    <Button
      onClick={() => navigate("/join")}
      className="h-12 px-8 rounded-full text-base font-semibold gap-2 bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/30 hover:-translate-y-0.5 transition-[transform,background-color]"
      data-testid="join-live-drill-btn"
    >
      <Radio className="w-5 h-5" /> Join Live Drill
    </Button>
  );

  // Use assignedDrills if available, otherwise fall back to single assignedDrill
  const drillsToShow = assignedDrills.length > 0 ? assignedDrills : (assignedDrill ? [assignedDrill] : []);
  const incompleteDrills = drillsToShow.filter(d => d.status !== "completed");
  const completedDrills = drillsToShow.filter(d => d.status === "completed");

  // Single drill button (when only one drill)
  const SingleDrillButton = ({ drill }) => {
    if (!drill) return null;
    
    const isCompleted = drill.status === "completed";
    const isPaused = drill.status === "paused";
    const isInProgress = drill.status === "in_progress";
    
    let buttonText = "Complete Coding Drill";
    if (isCompleted) {
      buttonText = `Drill Complete (${drill.completed_count}/${drill.total_questions})`;
    } else if (isPaused || isInProgress) {
      buttonText = "Continue Coding Drill";
    }
    
    return (
      <div className="mt-4">
        <Button
          onClick={() => onStartDrill && onStartDrill(drill)}
          disabled={isCompleted}
          className={`w-full max-w-md gap-2 ${
            isCompleted 
              ? "bg-gray-400 text-gray-200 cursor-not-allowed" 
              : "bg-orange-500 hover:bg-orange-600 text-white"
          }`}
          data-testid="drill-button"
        >
          <BookOpen className="w-4 h-4" />
          {buttonText}
        </Button>
        {!isCompleted && (
          <p className="text-sm text-muted-foreground mt-1">
            {drill.completed_count}/{drill.total_questions} Questions Completed
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1.5">
          <span>
            {drill.school_name && <span className="text-primary">{drill.school_name}: </span>}
            {drill.title}
          </span>
          <TrackBadge mode={drill.mode} testId="drill-button-track-badge" />
        </p>
      </div>
    );
  };

  // Multiple drills component (teacher-assigned drills across the student's classes)
  const MultipleDrillsButton = () => {
    if (drillsToShow.length === 0) {
      return null;
    }
    if (drillsToShow.length === 1) return <SingleDrillButton drill={drillsToShow[0]} />;
    
    return (
      <div className="mt-4 w-full max-w-md mx-auto">
        {/* Main button showing count of incomplete drills */}
        <Button
          onClick={() => setShowDrillList(!showDrillList)}
          className="w-full gap-2 bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="drill-button-multi"
        >
          <BookOpen className="w-4 h-4" />
          {incompleteDrills.length > 0 
            ? `${incompleteDrills.length} Coding Drill${incompleteDrills.length > 1 ? 's' : ''} Assigned`
            : `All ${completedDrills.length} Drills Complete`
          }
          <ChevronDown className={`w-4 h-4 transition-transform ${showDrillList ? 'rotate-180' : ''}`} />
        </Button>
        
        {/* Drill list dropdown */}
        {showDrillList && (
          <div className="mt-2 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
            {/* Incomplete drills first */}
            {incompleteDrills.map((drill) => (
              <button
                key={drill.drill_id}
                onClick={() => {
                  onStartDrill && onStartDrill(drill);
                  setShowDrillList(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-b-0"
                data-testid={`drill-item-${drill.drill_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <School className="w-3 h-3 text-primary" />
                      <span className="text-xs font-medium text-primary">
                        {drill.school_name}
                      </span>
                    </div>
                    <p className="font-medium text-sm mt-0.5 flex items-center gap-1.5">{drill.title}<TrackBadge mode={drill.mode} testId={`drill-item-track-badge-${drill.drill_id}`} /></p>
                    <p className="text-xs text-muted-foreground">
                      {drill.completed_count}/{drill.total_questions} completed
                      {drill.status === "in_progress" || drill.status === "paused" ? " • In Progress" : ""}
                    </p>
                  </div>
                  <div className="text-orange-500">
                    <BookOpen className="w-5 h-5" />
                  </div>
                </div>
              </button>
            ))}
            
            {/* Completed drills */}
            {completedDrills.length > 0 && (
              <>
                {incompleteDrills.length > 0 && (
                  <div className="px-4 py-2 bg-muted/30 text-xs text-muted-foreground font-medium">
                    Completed
                  </div>
                )}
                {completedDrills.map((drill) => (
                  <div
                    key={drill.drill_id}
                    className="w-full px-4 py-3 text-left bg-muted/20 border-b border-border last:border-b-0 opacity-60"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <School className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground font-medium">{drill.school_name}</span>
                        </div>
                        <p className="font-medium text-sm mt-0.5 text-muted-foreground flex items-center gap-1.5">{drill.title}<TrackBadge mode={drill.mode} /></p>
                        <p className="text-xs text-muted-foreground">
                          {drill.total_questions}/{drill.total_questions} completed
                        </p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Legacy DrillButton for backwards compatibility
  const DrillButton = () => <MultipleDrillsButton />;

  if (mode === "daily") {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <Calendar className="w-5 h-5 text-primary" />
          <span className="text-sm text-muted-foreground">{today}</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4" data-testid="page-title">
          <span className="text-primary">CodingDrills</span>.au
        </h1>
        <div className="mb-4">
          <JoinLiveButton />
        </div>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Complete {dailyProgress.required} warm up questions to unlock bonus challenges!
        </p>
        
        <div className="max-w-md mx-auto">
          {/* Session score for anonymous users */}
          {!user && displayScore > 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              Session Score: <span className="font-medium text-secondary">{displayScore} pts</span>
              <span className="text-xs ml-1">(login to save)</span>
            </p>
          )}
          
          {/* Drill Button */}
          <DrillButton />
        </div>
      </motion.div>
    );
  }

  // Challenge mode
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center mb-8"
    >
      <div className="flex items-center justify-center gap-2 mb-2">
        <Zap className="w-5 h-5 text-secondary" />
        <span className="text-sm text-muted-foreground">Challenge Mode</span>
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-3" data-testid="page-title">
        Bonus <span className="text-secondary">Challenges</span>
      </h1>
      
      <div className="max-w-md mx-auto mb-4">
        {/* Session score for anonymous users */}
        {!user && displayScore > 0 && (
          <p className="text-sm text-muted-foreground mt-2">
            Session Score: <span className="font-medium text-secondary">{displayScore} pts</span>
            <span className="text-xs ml-1">(login to save)</span>
          </p>
        )}
        
        {/* Drill Button */}
        <DrillButton />
        
        {/* Cave Bat Button - Shows when user has 3+ challenges but hasn't reached limit */}
        {user && challengesInfo.challenges_today >= 3 && challengesInfo.challenges_today < challengesInfo.daily_limit && onPlayCaveBat && (
          <div className="mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onPlayCaveBat}
              className="gap-2 text-purple-600 border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-500/10 dark:text-purple-400"
              data-testid="cave-bat-btn"
            >
              <span className="text-lg">🦇</span>
              Play Cave Bat
            </Button>
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {!dailyCompleted && (
          <Button
            variant="outline"
            size="sm"
            onClick={onBackToWarmup}
            data-testid="back-to-warmup-btn"
          >
            Back to Warm Up
          </Button>
        )}
        
        {/* Streak indicator */}
        {streakCount >= 3 && (
          <div className="flex items-center gap-1 text-sm text-secondary bg-secondary/10 px-3 py-1 rounded-full">
            <Zap className="w-4 h-4" />
            <span>{streakCount} streak!</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
